import React from 'react'
import ReactModal from 'react-modal'
import classNames from 'classnames'

import './styles.scss'
const baseClass = 'modal-component'

/**
 * See params which appear in child Modal component below
 * @see https://github.com/reactjs/react-modal#usage
 * Usage:
 * 1.
 * import { modal } from 'store/entities/modal/actions'
 *
 * const mapDispatchToProps = {
 *   showModal: modal.show,
 * }
 *
 * 2. Create you modal component or use existing one. This component get all props from link below
 * @see https://github.com/reactjs/react-modal#usage
 *
 * class MyPopupComponent extends Component {
 *   render () {
 *     const {
 *       onAfterOpen={afterOpenFn}
 *       onRequestClose={requestCloseFn}
 *       closeTimeoutMS={n}
 *       contentLabel="Modal"
 *     } = this.props
 *     return (
 *       <div>
 *         <button onClick={() => onRequestClose()}>Close Button</button>
 *         {'Popup Content Here'}
 *       </div>
 *     )
 *   }
 * }
 *
 * 3. Use yours popup with action
 *
 * <button
 *   onClick={() => showModal(MyPopupComponent, {...restProps})}
 * >
 *   Show Modal Button
 * </button>
 *            or
 * <button
 *   onClick={() => showModal(<MyPopupComponent onOk={() => console.log('executed onOk')} />, {...restProps})}
 * >
 *   Show Modal Button
 * </button>
 */

interface IModalProps {
  children: React.ReactElement
  className: any
  size?: string
  restProps?: { important?: boolean; size: any }
  important?: boolean
}

const Modal: React.FC<IModalProps> = ({ children, size = 'lg', important, restProps = {}, className, ...rest }) => {
  size = restProps.size || size
  const shouldCloseOnOverlayClick = !(important || restProps.important)
  return (
    <ReactModal
      isOpen={false}
      {...rest}
      className={{
        base: classNames('modal-component', className, {
          [`${baseClass}--xxs`]: size === 'xxs',
          [`${baseClass}--xs`]: size === 'xs',
          [`${baseClass}--mm`]: size === 'mm',
          [`${baseClass}--sm`]: size === 'sm',
          [`${baseClass}--md`]: size === 'md',
          [`${baseClass}--lg`]: size === 'lg',
          [`${baseClass}--elg`]: size === 'elg', // extra large
          [`${baseClass}__stretch`]: size === 'stretch',
        }),
        afterOpen: `${baseClass}--open`,
        beforeClose: `${baseClass}--before-close`,
      }}
      overlayClassName={{
        base: classNames('overlay', { [`${baseClass}__stretch-base`]: size === 'stretch' }),
        afterOpen: 'fade-in',
        beforeClose: 'fade-out',
      }}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      {...restProps}
    >
      {children
        ? React.isValidElement(children)
          ? React.cloneElement(children, { ...rest, ...restProps })
          : React.createElement(children, { ...rest, ...restProps })
        : null}
    </ReactModal>
  )
}

Modal.displayName = 'Modal'

export default Modal
